// PWA install + update helpers — register SW, capture beforeinstallprompt,
// detect iOS/standalone, expose "yeni sürüm hazır" state to UI.
let deferredPrompt = null;
let waitingRegistration = null;   // yeni SW hazır ama activate bekliyor
let updateAvailable = false;
const listeners = new Set();
const updateListeners = new Set();

function emit(state) {
  listeners.forEach((cb) => {
    try { cb(state); } catch {}
  });
}

function emitUpdate() {
  const state = { available: updateAvailable };
  updateListeners.forEach((cb) => {
    try { cb(state); } catch {}
  });
}

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

export function isIOS() {
  const ua = window.navigator.userAgent || "";
  const isiOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Modern iPadOS (13+) reports as Mac — sniff for touch to catch it.
  const isIPadOS = ua.includes("Macintosh") && "ontouchend" in document;
  return isiOSDevice || isIPadOS;
}

export function canPromptInstall() {
  return !!deferredPrompt;
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  const evt = deferredPrompt;
  deferredPrompt = null;
  evt.prompt();
  const choice = await evt.userChoice;
  emit({ canPrompt: false, standalone: isStandalone() });
  return choice; // { outcome: "accepted" | "dismissed" }
}

export function onPwaState(cb) {
  listeners.add(cb);
  // fire once immediately
  cb({ canPrompt: canPromptInstall(), standalone: isStandalone(), ios: isIOS() });
  return () => listeners.delete(cb);
}

/**
 * SW yeni sürüm hazır olduğunda çağrılır.
 * @returns unsub fonksiyonu
 */
export function onUpdateAvailable(cb) {
  updateListeners.add(cb);
  cb({ available: updateAvailable });
  return () => updateListeners.delete(cb);
}

/**
 * "Güncelle" butonuna basıldığında: bekleyen SW'ye skip-waiting sinyali gönder,
 * kısa gecikme sonrası sayfayı hard reload et — yeni bundle yüklensin.
 */
export function applyUpdate() {
  try {
    const w = waitingRegistration?.waiting || null;
    if (w) w.postMessage({ type: "SKIP_WAITING" });
  } catch {}
  // controllerchange dinleyicisi bizi tekrar yükleyecek, ama safety olarak 800ms sonra force reload
  setTimeout(() => {
    try { window.location.reload(); } catch {}
  }, 800);
}

export function initPwa() {
  // Capture the install prompt.
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit({ canPrompt: true, standalone: isStandalone(), ios: isIOS() });
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try { localStorage.setItem("uc_pwa_installed", "1"); } catch {}
    emit({ canPrompt: false, standalone: true, ios: isIOS() });
  });

  // Register the service worker + update check flow.
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });

        // Yeni SW keşfedildiğinde (statechange → "installed" && controller varsa güncelleme var)
        const trackInstalling = (installing) => {
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              waitingRegistration = reg;
              updateAvailable = true;
              emitUpdate();
            }
          });
        };
        if (reg.waiting && navigator.serviceWorker.controller) {
          waitingRegistration = reg;
          updateAvailable = true;
          emitUpdate();
        }
        if (reg.installing) trackInstalling(reg.installing);
        reg.addEventListener("updatefound", () => {
          if (reg.installing) trackInstalling(reg.installing);
        });

        // Sayfa her fokus aldığında SW'ye "yeni versiyon var mı?" diye sor (max 60sn'de bir).
        let lastCheck = 0;
        const checkForUpdate = () => {
          const now = Date.now();
          if (now - lastCheck < 60_000) return;
          lastCheck = now;
          reg.update().catch(() => {});
        };
        checkForUpdate();
        window.addEventListener("focus", checkForUpdate);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") checkForUpdate();
        });
        // Her açılışta ve 60sn'de bir zorla kontrol et
        setInterval(checkForUpdate, 60_000);

        // Yeni SW controller olduğunda sayfayı sessizce reload et (skip-waiting sonrası)
        let refreshing = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });
      } catch {}
    });

    // BroadcastChannel: SW yeni versiyon activate ettiğinde
    try {
      const bc = new BroadcastChannel("milyemsoft-sw");
      bc.addEventListener("message", (ev) => {
        if (ev.data?.type === "UPDATE_ACTIVATED") {
          // zaten controllerchange tetiklenecek — safety
        }
      });
    } catch {}
  }
}
